import { getStore as composeStore } from "owa-mail-compose-store";
import { lazyDiscardCompose, lazyLoadDraftToCompose } from "owa-mail-compose-actions";
import { lazyNewMessage } from "owa-mail-message-actions";
import selectFolderWithFallback from "./selectFolderWithFallback";
import getSelectedFolderMailboxInfoOrDefault from "owa-mail-store/lib/utils/getSelectedFolderMailboxInfoOrDefault";
import { getQueryStringParametersFromURL } from "owa-content-handler/lib/utils/queryStringHelper";
import { convertRestIdToEwsId } from "owa-identifiers";
import { lazyBizChatHandleoff, type McpWidgetComposeParameters, } from "owa-bizchat-context-handlers";
import { resolveMailboxFromRouteKey } from "owa-mailbox-info/lib/resolveMailboxFromRouteKey";
import { getApplinkAccountRedirectContext, tryRedirectForSharedOrUnresolvedAccount, } from "./tryRedirectForSharedOrUnresolvedAccount";

export interface MailFolderRouteParameters {
    folderId?: string;
    draftId?: string;
    routeKey?: string;
    mcpHandoffMode?: McpWidgetComposeParameters["mcpHandoffMode"];
}

export async function mailComposeNavigationRouteHandler(parameters: MailFolderRouteParameters): Promise<void> {
    const applinkAccountRedirectContext = getApplinkAccountRedirectContext(parameters.routeKey);
    selectFolderWithFallback(parameters.folderId);
    
    // Resolve mailbox from universal link route key when flighted,
    // otherwise fall back to the selected folder mailbox
    const resolvedMailboxInfo = parameters.routeKey
        ? await resolveMailboxFromRouteKey(parameters.routeKey, "ComposeFromApplink")
        : undefined;
    
    if (!resolvedMailboxInfo &&
        (await tryRedirectForSharedOrUnresolvedAccount(applinkAccountRedirectContext))) {
        return;
    }
    
    if (parameters.draftId) {
        const isMcpComposeHandoff = parameters.mcpHandoffMode === "auto";
        const convertedId = convertRestIdToEwsId(parameters.draftId); // If already in correct format, this should return the same id
        await lazyLoadDraftToCompose.importAndExecute({
            Id: convertedId ? convertedId : parameters.draftId,
            mailboxInfo: resolvedMailboxInfo || getSelectedFolderMailboxInfoOrDefault(),
        }, null /* sxsId */, "Route" /* actionSource */, undefined /* openTarget */, isMcpComposeHandoff
            ? { bizChatHandoffData: { preserveBizChatSession: true, isMcpComposeHandoff } }
            : undefined /* options */);
    }
    else {
        await lazyNewMessage.importAndExecute("Route", {
            ...(resolvedMailboxInfo ? { mailboxInfo: resolvedMailboxInfo } : {}),
        });
    }
}

export function mailComposeCleanupRouteHandler(): Promise<boolean> | boolean {
    const primaryComposeId = composeStore().primaryComposeId;
    if (primaryComposeId) {
        const viewState = composeStore().viewStates.get(primaryComposeId);
        if (viewState) {
            return lazyDiscardCompose.importAndExecute(viewState).then(() => false, // Confirm ('Discard draft') means we should not block navigation
            () => true); // Cancel ('Dont' discard') means we should block navigation
        }
    }
    return false;
}

const ApplinkActionSource = "ApplinkRoute";

export async function mailComposeBizChatRouteHandler(): Promise<void> {
    const mailboxInfo = getSelectedFolderMailboxInfoOrDefault();
    
    const eventTimestamp = Date.now();
    const { h, h_env, refId, action } = getQueryStringParametersFromURL();
    const bizChatHandoff = await lazyBizChatHandleoff.import();
    
    bizChatHandoff(mailboxInfo, ApplinkActionSource /* source */, eventTimestamp /* eventTimestamp */, {
        handoffId: h,
        handoffEnv: h_env,
        refId,
        action,
    });
}
